
import Field from "@/shared/components/intrinsic/Field/Field"
import { inputValue } from "@/utils/common"

export interface EventBasicsData {
  name: string
  description: string
  recurrenceMode: "daily" | "specific_days" | "specific_dates"
  selectedDays: string[]
  startDate: string
  startTime: string
  endDate: string
  endTime: string
}

interface Step1EventBasicsProps {
  data: EventBasicsData
  onChange: (data: EventBasicsData) => void
}

const DAYS_OF_WEEK = [
  { value: "Sunday", label: "Sun" },
  { value: "Monday", label: "Mon" },
  { value: "Tuesday", label: "Tue" },
  { value: "Wednesday", label: "Wed" },
  { value: "Thursday", label: "Thu" },
  { value: "Friday", label: "Fri" },
  { value: "Saturday", label: "Sat" }
]

function Step1EventBasics(props: Step1EventBasicsProps) {
  const { data, onChange } = props

  function toggleDay(day: string) {
    const newDays = data.selectedDays.includes(day)
      ? data.selectedDays.filter(d => d !== day)
      : [...data.selectedDays, day]
    onChange({ ...data, selectedDays: newDays })
  }

  function toggleSelectAll() {
    if (data.selectedDays.length === DAYS_OF_WEEK.length) {
      onChange({ ...data, selectedDays: [] })
    } else {
      onChange({ ...data, selectedDays: DAYS_OF_WEEK.map(d => d.value) })
    }
  }

  return (
    <div className="step1-event-basics">
      <div className="step1-event-basics__row">
        <div className="step1-event-basics__field">
          <label className="step1-event-basics__label">
            Scheduler's Name
            <span className="step1-event-basics__hint">Insert the Scheduler name*</span>
          </label>
          <Field
            placeholder="Event Name"
            value={data.name}
            onChange={inputValue(name => onChange({ ...data, name }))}
          />
        </div>

        <div className="step1-event-basics__field">
          <label className="step1-event-basics__label">
            Internal Notes
            <span className="step1-event-basics__hint">Visible only to your team*</span>
          </label>
          <textarea
            className="step1-event-basics__textarea"
            placeholder="Add internal notes — e.g., purpose, duration, or context of this schedule."
            value={data.description}
            onChange={e => onChange({ ...data, description: e.target.value })}
            rows={5}
          />
        </div>
      </div>

      <div className="step1-event-basics__section">
        <label className="step1-event-basics__section-label">Recurrence Pattern</label>
        <div className="step1-event-basics__toggle">
          <button
            type="button"
            className={`step1-event-basics__toggle-btn ${data.recurrenceMode === "daily" ? "step1-event-basics__toggle-btn--active" : ""}`}
            onClick={() => onChange({ ...data, recurrenceMode: "daily", selectedDays: [] })}
          >
            Daily
          </button>
          <button
            type="button"
            className={`step1-event-basics__toggle-btn ${data.recurrenceMode === "specific_days" ? "step1-event-basics__toggle-btn--active" : ""}`}
            onClick={() => onChange({ ...data, recurrenceMode: "specific_days", startDate: "", endDate: "" })}
          >
            Specific Days
          </button>
          <button
            type="button"
            className={`step1-event-basics__toggle-btn ${data.recurrenceMode === "specific_dates" ? "step1-event-basics__toggle-btn--active" : ""}`}
            onClick={() => {
              const today = new Date().toISOString().split("T")[0] // Format: YYYY-MM-DD
              onChange({
                ...data,
                recurrenceMode: "specific_dates",
                selectedDays: [],
                startDate: data.startDate || today
              })
            }}
          >
            Specific Dates
          </button>
        </div>
      </div>

      {/* Time Selection - shown for all modes */}
      <div className="step1-event-basics__section">
        <label className="step1-event-basics__section-label">Time Range</label>
        <div className="step1-event-basics__datetime">
          <Field
            type="time"
            placeholder="Start Time"
            value={data.startTime}
            onChange={inputValue(startTime => onChange({ ...data, startTime }))}
          />
          <Field
            type="time"
            placeholder="End Time"
            value={data.endTime}
            onChange={inputValue(endTime => onChange({ ...data, endTime }))}
          />
        </div>
      </div>

      {/* Days of Week - only for specific_days mode */}
      {data.recurrenceMode === "specific_days" && (
        <div className="step1-event-basics__section">
          <label className="step1-event-basics__section-label">
            Days Of The Week
            <span className="step1-event-basics__hint">Select one or more days (repeats weekly)*</span>
          </label>
          <div className="step1-event-basics__days">
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day.value}
                type="button"
                className={`step1-event-basics__day-btn ${
                  data.selectedDays.includes(day.value) ? "step1-event-basics__day-btn--selected" : ""
                }`}
                onClick={() => toggleDay(day.value)}
              >
                {day.label}
              </button>
            ))}
            <label className="step1-event-basics__select-all">
              <input
                type="checkbox"
                checked={data.selectedDays.length === DAYS_OF_WEEK.length}
                onChange={toggleSelectAll}
              />
              SELECT ALL
            </label>
          </div>
        </div>
      )}

      {/* Date Range - only for specific_dates mode */}
      {data.recurrenceMode === "specific_dates" && (
        <>
          <div className="step1-event-basics__section">
            <label className="step1-event-basics__section-label">Start Date</label>
            <Field
              type="date"
              value={data.startDate}
              onChange={inputValue(startDate => onChange({ ...data, startDate }))}
            />
          </div>

          <div className="step1-event-basics__section">
            <label className="step1-event-basics__section-label">End Date</label>
            <Field
              type="date"
              value={data.endDate}
              onChange={inputValue(endDate => onChange({ ...data, endDate }))}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default Step1EventBasics
